import React, { useMemo } from 'react';
import FrequentRolePills from './FrequentRolePills';
import Pill from '../interaction/Pill';
import './RolesGrid.css';

function groupRoles(roles) {
  const map = {};
  roles.forEach((role) => {
    const prefix = role.id.split('_')[0];
    if (!map[prefix]) map[prefix] = [];
    map[prefix].push(role);
  });
  return Object.entries(map)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([prefix, items]) => ({ prefix, items: items.sort((a, b) => a.id.localeCompare(b.id)) }));
}

/**
 * Reusable role selector — groups all roles by first-word prefix.
 * Drop-in replacement for a multi-select Autocomplete wherever role
 * selection is needed.
 *
 * Props:
 *   allRoles        — role objects { id, name }
 *   selectedRoleIds — currently selected IDs
 *   onToggle(id)    — called on pill click
 *   showFrequent    — render the "Quick add" shortcut row above the grid (default true).
 *                     Set false when the caller renders those shortcuts itself.
 *   categoryColumns — how many prefix groups sit side by side (default 1, i.e. stacked).
 *   variant         — 'plain' (default): a caption over a fixed 3-column pill grid.
 *                     'cards': each role type gets its own card and the pills inside
 *                     keep their natural width and wrap.
 */
const RolesGrid = React.memo(function RolesGrid({
  allRoles,
  selectedRoleIds,
  onToggle,
  showFrequent = true,
  categoryColumns = 1,
  variant = 'plain',
}) {
  // Re-grouping and re-sorting every role on each parent render was part of what
  // made typing in the form feel sluggish.
  const groups = useMemo(() => groupRoles(allRoles), [allRoles]);
  const isCards = variant === 'cards';
  // Smallest step inside cards so a card can hold a couple of columns of roles
  // without the pills dominating it.
  const pillSize = isCards ? 'xs' : 'sm';

  return (
    <div>
      {showFrequent && (
        <FrequentRolePills
          allRoles={allRoles}
          selectedRoleIds={selectedRoleIds}
          onToggle={onToggle}
        />
      )}
      <div className="rg-groups" style={{ '--rg-columns': categoryColumns }}>
        {groups.map(({ prefix, items }) => (
          <div key={prefix} className={`rg-group rg-group--${isCards ? 'card' : 'plain'}`}>
            <span className="rg-label">{prefix}</span>
            <div className="rg-pills">
              {items.map((role) => (
                <Pill
                  key={role.id}
                  label={role.id}
                  variant={selectedRoleIds.includes(role.id) ? 'primary' : 'light'}
                  size={pillSize}
                  onClick={() => onToggle(role.id)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

export default RolesGrid;
